import React, { useEffect } from 'react';
import { Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faTimes, faRobot, faBroom } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import ChatContent from './content/ChatContent';
import ChatProvider, { useChatContext } from '../../providers/ChatProvider';
import { useAuth } from '../../contexts/AuthContext';
import './Chat.css';

const ChatButton = () => {
  const { isOpen, toggleChat, messages } = useChatContext();
  const unreadCount = messages.filter(msg => msg.sender === 'assistant' && !msg.read).length;

  return (
    <Button
      variant="primary"
      className={classNames('chat-toggle-button', { 'chat-open': isOpen })}
      onClick={toggleChat}
      size="lg"
    >
      <FontAwesomeIcon icon={isOpen ? faTimes : faComments} />
      {!isOpen && unreadCount > 0 && (
        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
          {unreadCount}
        </span>
      )}
    </Button>
  );
};

const ChatWindow = () => {
  const { 
    isOpen, 
    toggleChat, 
    clearChat, 
    messages, 
    getConversationSummary,
    scrollToBottomHandler,
    scrollToBottom
  } = useChatContext();

  const summary = getConversationSummary();

  useEffect(() => {
    if (scrollToBottom) {
      scrollToBottomHandler();
    }
  }, [scrollToBottom, scrollToBottomHandler]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        toggleChat();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, toggleChat]);

  if (!isOpen) return null;

  return (
    <div className="chat-window" onClick={(e) => e.target === e.currentTarget && toggleChat()}>
      <div className="chat-container">
        <Card className="h-100 shadow-lg">
          <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon={faRobot} className="me-2" />
              <div>
                <h6 className="mb-0">Galaxy AI Assistant</h6>
                <small className="opacity-75">
                  {summary.totalMessages > 0 
                    ? `${summary.totalMessages} mensajes en esta conversación`
                    : 'Pregúntame sobre tus datos'
                  }
                </small>
              </div>
            </div>
            <div className="d-flex gap-2">
              {messages.length > 0 && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={clearChat}
                  title="Limpiar conversación"
                >
                  <FontAwesomeIcon icon={faBroom} />
                </Button>
              )}
              <Button
                variant="outline-light"
                size="sm"
                onClick={toggleChat}
                title="Cerrar chat"
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          </Card.Header>

          <Card.Body className="p-0 h-100">
            <ChatContent />
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

const ChatWidget = () => {
  const { hasRole } = useAuth();
  
  // Solo mostrar el chat si el usuario es admin o super_admin
  if (!hasRole('admin') && !hasRole('super_admin')) {
    return null;
  }

  return (
    <ChatProvider>
      <div className="chat-widget">
        <ChatButton />
        <ChatWindow />
      </div>
    </ChatProvider>
  );
};

export default ChatWidget; 